import { matchSubdomainForSSO } from '@acme/b2b/sso/match-subdomain-for-sso';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { AuthenticationType } from '@b2b/common/services/auth/types';
import { BugsnagService, BugsnagServiceModule } from '@b2b/common/services/bugsnag';
import { ACMEResponse, extractACMEErrorType, WrappedACMEErrorResponse } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import postRobot from '@krakenjs/post-robot';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, {
  IDocumentService,
  IHttpResponse,
  IIntervalService,
  IScope,
  ITimeoutService,
} from 'angular';

LoginController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  '$interval',
  '$document',
  'ApiService',
  'ValidateService',
  'NoticeFactory',
  'PreFlightCheck',
  'AuthenticationService',
  'BugsnagService',
];

function LoginController(
  $scope: IScope & { login: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  $interval: IIntervalService,
  $document: IDocumentService,
  ApiService: ApiService,
  ValidateService: TODO,
  NoticeFactory: NoticeFactory,
  PreFlightCheck: {
    data: ACMEResponse<{
      httpHeaderName: string;
      httpHeaderValue: string;
      authenticationType: AuthenticationType;
    }>;
  },
  authenticationService: AuthenticationService,
  bugsnagService: BugsnagService,
) {
  const ctrl = {
    isSubmitted: false,

    email: '',
    password: '',
    authenticationType: 'LEGACY' as AuthenticationType,
    authenticationWindow: null as Window | null,

    notice: new NoticeFactory(),

    get isLDAPTenant() {
      return PreFlightCheck.data && PreFlightCheck.data.success;
    },

    init: function () {
      if ($state.params && !$state.params.hasOwnProperty('token')) {
        console.log('LoginController::init -> token');
        const state = authenticationService.getRedirectState();

        if (state) {
          $state.go(state);
        }
      }

      ctrl.authenticationType = ctrl.getAuthenticationType();

      if (ctrl.authenticationType === 'SSO') {
        bugsnagService.addFeatureFlag('SSO');
        $document.on('click', () => ctrl.focusAuthenticationWindow());

        $scope.$on('$destroy', () => {
          $document.off('click');
        });
      }

      // Listeners
      $scope.$on('LoginTokenController::login::success', (e, data) => {
        ctrl.success.completeLogin.apply($scope, data);
      });
      $scope.$on('LoginTokenController::login::error', (e, data) => {
        ctrl.error.completeLogin.apply($scope, data);
      });
    },

    isValidEmail: function () {
      if (ctrl.isSubmitted) {
        return ValidateService.validate([{ email: ctrl.email }]);
      }
    },

    isValidPassword: function () {
      if (ctrl.isSubmitted) {
        return ValidateService.validate([{ len: { value: ctrl.password } }]);
      }
    },

    completeLogin: function () {
      const props = {
        email: ctrl.email,
        password: ctrl.password,
      } as { email: string; password: string; header?: { name: string; value: string } };

      ctrl.notice.startLoad();

      if (ctrl.isLDAPTenant) {
        /**
         *  Setting 2 flags for future LDAP customers
         *  We should make this configurable from the administration
         *  panel and integrate it with preflight check
         *  in order to toggle the configurable security message
         */
        props.header = {
          name: PreFlightCheck.data.payload.httpHeaderName,
          value: PreFlightCheck.data.payload.httpHeaderValue,
        };

        authenticationService.login(props)
          .then((response) => ctrl.success.completeLogin(response))
          .catch((response) => ctrl.error.completeLogin(response));
      } else {
        if (ValidateService.validate([{ email: ctrl.email }, { len: { value: ctrl.password } }])) {
          authenticationService.login(props)
            .then((response) => ctrl.success.completeLogin(response))
            .catch((response) => ctrl.error.completeLogin(response));
        } else {
          ctrl.notice.endLoad();
          ctrl.notice.showErrorMessages([{ message: 'Please correct the errors below.' }]);
        }
      }
    },

    complete: function () {
      ctrl.notice.hideMessages();
      ctrl.isSubmitted = true;

      ctrl.completeLogin();
    },

    getSubdomainForSSO: function () {
      return matchSubdomainForSSO(window.location.href);
    },

    getAuthenticationType: function () {
      if (ctrl.isLDAPTenant) {
        return PreFlightCheck.data.payload.authenticationType ?? 'LDAP';
      }

      return ctrl.getSubdomainForSSO().length > 0 ? 'SSO' : 'LEGACY';
    },

    focusAuthenticationWindow: function () {
      if (ctrl.authenticationWindow != null && !ctrl.authenticationWindow.closed) {
        ctrl.authenticationWindow.focus();
        return true;
      }

      return false;
    },

    onWindowClosed: function (targetWindow: Window | null, callback: () => void) {
      const timer = $interval(() => {
        if (targetWindow?.closed) {
          $interval.cancel(timer);

          callback();
        }
      }, 100);
    },

    loginWithSSO: function () {
      if (ctrl.focusAuthenticationWindow()) {
        return;
      }

      const ssoSubdomain = ctrl.getSubdomainForSSO();
      const targetUrl = new URL('/v2/b2b/sso/auth', window.CONFIG.B2B_API_BASE_URL);
      targetUrl.searchParams.append('routing-subdomain-name', ssoSubdomain);

      ctrl.notice.startLoad();

      ctrl.authenticationWindow = window.open(targetUrl, 'ACME_SSO', 'width=800,height=800');

      ctrl.onWindowClosed(ctrl.authenticationWindow, () => {
        ctrl.notice.endLoad();
      });

      const listener = postRobot.on('sso.callback', { window: ctrl.authenticationWindow }, async (event) => {
        const eventData = event.data.data;
        const RelayState = eventData.RelayState;

        if (RelayState !== null) {
          return authenticationService
            .ssoCallback({ RelayState })
            .then((response) => {
              authenticationService.send({
                type: 'sso.callback.success',
                params: { session: response.data.session },
              });

              return authenticationService.completeLogin(response).then(() => ctrl.success.complete());
            })
            .catch((response: IHttpResponse<WrappedACMEErrorResponse>) => {
              console.error(response);
              bugsnagService.notify(new Error(response.data.error), (event) => {
                event.context = 'sso.callback.error';
                event.addMetadata('response', response.data);
              });
              ctrl.error.completeLogin(response);
            })
            .finally(() => {
              listener.cancel();

              ctrl.authenticationWindow?.close();
              ctrl.authenticationWindow = null;
            });
        }

        listener.cancel();
      });
    },

    success: {
      completeLogin: function (response) {
        authenticationService
          .completeLogin(response)
          .then(() => ctrl.success.complete())
          .catch((response) => ctrl.error.completeLogin(response));
      },

      complete: function () {
        $timeout(() => {
          const state = authenticationService.getRedirectState();

          if (state) {
            $state.go(state);
          }

          $scope.$emit('LoginController::success');
          ctrl.notice.endLoad();
        }, 500);
      },
    },

    error: {
      completeLogin: function (response: IHttpResponse<WrappedACMEErrorResponse>) {
        ctrl.error.complete(response);
      },

      complete: function (response: IHttpResponse<WrappedACMEErrorResponse>) {
        ctrl.notice.endLoad();

        const oldPassword = ctrl.password;
        ctrl.password = '';

        if (response.status === 401 || response.status === 440) {
          authenticationService.destroy();

          $scope.$emit('LoginController::error::unauthorized');
        } else {
          $scope.$emit('LoginController::error');
        }

        const errorType = extractACMEErrorType(response.data);

        switch (errorType) {
          case 'PASSWORD_EXPIRED': {
            $state.go('resetPassword.passwordExpired', {
              email: ctrl.email,
              oldPassword: oldPassword,
              error: response.data.errorData,
            });
            return;
          }

          default: {
            ctrl.notice.showErrorMessages([{ message: response.data.error }]);
            return;
          }
        }
      },
    },
  };

  $scope.login = ctrl;
  ctrl.init();
}

export const LoginControllerModule = angular
  .module('acme.view.login.controllers', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.factory.notice',
    AuthenticationServiceModule,
    BugsnagServiceModule,
  ])
  .controller('LoginController', LoginController).name;
